<script setup>
import { ref, computed } from 'vue';
import { Card } from 'primevue';
import ANButton from '@/components/common/AnButton.vue';

const props = defineProps({
  onSubmit: {
    type: Function,
    required: true
  },
  isSubmitted: {
    type: Boolean,
    default: false
  }
});

// Form data
const clientInfo = ref({
  name: '',
  email: '',
  age: ''
});

// Validation
const errors = ref({
  name: '',
  email: '',
  age: ''
});

// Computed validation
const isFormValid = computed(() => {
  return clientInfo.value.name.trim() &&
         clientInfo.value.email.trim() &&
         isValidEmail(clientInfo.value.email) &&
         clientInfo.value.age &&
         isValidAge(clientInfo.value.age);
});

// Email validation
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Age validation
const isValidAge = (age) => {
  const ageNum = parseInt(age);
  return ageNum >= 18 && ageNum <= 100;
};

// Real-time validation
const validateField = (field) => {
  switch (field) {
    case 'name':
      errors.value.name = !clientInfo.value.name.trim()
        ? 'Full name is required'
        : clientInfo.value.name.trim().length < 2
        ? 'Name must be at least 2 characters'
        : '';
      break;
    case 'email':
      if (!clientInfo.value.email.trim()) {
        errors.value.email = 'Email address is required';
      } else if (!isValidEmail(clientInfo.value.email)) {
        errors.value.email = 'Please enter a valid email address';
      } else {
        errors.value.email = '';
      }
      break;
    case 'age':
      if (!clientInfo.value.age) {
        errors.value.age = 'Age is required';
      } else if (!isValidAge(clientInfo.value.age)) {
        errors.value.age = 'Age must be between 18 and 100';
      } else {
        errors.value.age = '';
      }
      break;
  }
};

// Handle form submission
const handleSubmit = (event) => {
  // Prevent any form submission
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // Validate all fields
  validateField('name');
  validateField('email');
  validateField('age');

  if (isFormValid.value) {
    props.onSubmit({
      name: clientInfo.value.name.trim(),
      email: clientInfo.value.email.trim().toLowerCase(),
      age: parseInt(clientInfo.value.age)
    });
  }
};

// Handle input changes
const handleInput = (field) => {
  validateField(field);
};
</script>

<template>
  <div class="client-info-form">
    <!-- Header Section -->
    <div class="form-header">
      <h2 class="form-title">Let's Get to Know You</h2>
      <p class="form-subtitle">We need a few details to personalize your investment recommendations</p>

      <!-- Progress Indicator -->
      <div class="progress-indicator">
        <div class="progress-steps">
          <div class="step active">
            <div class="step-number">1</div>
            <span class="step-label">Personal Info</span>
          </div>
          <div class="step-connector"></div>
          <div class="step">
            <div class="step-number">2</div>
            <span class="step-label">Risk Assessment</span>
          </div>
          <div class="step-connector"></div>
          <div class="step">
            <div class="step-number">3</div>
            <span class="step-label">Your Portfolio</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Form Card -->
    <Card class="info-form-card">
      <template #content>
        <div class="client-form">
          <!-- Full Name Input -->
          <div class="form-group">
            <label for="fullName" class="form-label">
              <span class="label-text">Full Name</span>
              <span class="required-indicator">*</span>
            </label>
            <div class="input-wrapper">
              <input
                id="fullName"
                v-model="clientInfo.name"
                @input="handleInput('name')"
                @blur="validateField('name')"
                type="text"
                placeholder="Enter your full name"
                class="form-input"
                :class="{ 'error': errors.name }"
                :disabled="isSubmitted"
              />
              <div class="input-icon">
                <i class="pi pi-user"></i>
              </div>
            </div>
            <div v-if="errors.name" class="error-message">{{ errors.name }}</div>
          </div>

          <!-- Email Input -->
          <div class="form-group">
            <label for="email" class="form-label">
              <span class="label-text">Email Address</span>
              <span class="required-indicator">*</span>
            </label>
            <div class="input-wrapper">
              <input
                id="email"
                v-model="clientInfo.email"
                @input="handleInput('email')"
                @blur="validateField('email')"
                type="email"
                placeholder="Enter your email address"
                class="form-input"
                :class="{ 'error': errors.email }"
                :disabled="isSubmitted"
              />
              <div class="input-icon">
                <i class="pi pi-envelope"></i>
              </div>
            </div>
            <div v-if="errors.email" class="error-message">{{ errors.email }}</div>
          </div>

          <!-- Age Input -->
          <div class="form-group">
            <label for="age" class="form-label">
              <span class="label-text">Age</span>
              <span class="required-indicator">*</span>
            </label>
            <div class="input-wrapper">
              <input
                id="age"
                v-model="clientInfo.age"
                @input="handleInput('age')"
                @blur="validateField('age')"
                type="number"
                min="18"
                max="100"
                placeholder="Enter your age"
                class="form-input"
                :class="{ 'error': errors.age }"
                :disabled="isSubmitted"
              />
              <div class="input-icon">
                <i class="pi pi-calendar"></i>
              </div>
            </div>
            <div v-if="errors.age" class="error-message">{{ errors.age }}</div>
          </div>

          <!-- Privacy Notice -->
          <div class="privacy-notice">
            <div class="notice-icon">
              <i class="pi pi-shield"></i>
            </div>
            <div class="notice-text">
              <p><strong>Privacy Protected:</strong> Your personal information is encrypted and secure. We use this data solely to provide personalized investment recommendations.</p>
            </div>
          </div>

          <!-- Submit Button -->
          <div class="form-actions">
            <ANButton
              :label="'Continue to Assessment'"
              @click="handleSubmit"
              :disabled="!isFormValid || isSubmitted"
              class="continue-button"
              type="button"
            />
            <p class="form-note">All fields are required to proceed</p>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.client-info-form {
  max-width: 600px;
  margin: 0 auto;
  font-family: "Satoshi-Variable", sans-serif;
}

.form-header {
  text-align: center;
  margin-bottom: 32px;
}

.form-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin-bottom: 12px;
  background: linear-gradient(135deg, var(--an-primary) 0%, var(--an-secondary) 50%, var(--an-tertiary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.form-subtitle {
  font-size: 1.125rem;
  color: var(--an-text-secondary);
  margin-bottom: 32px;
  line-height: 1.5;
}

.progress-indicator {
  margin-bottom: 20px;
}

.progress-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 400px;
  margin: 0 auto;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.step-number {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  background: var(--an-gray3);
  color: var(--an-gray2);
  transition: all 0.3s ease;
}

.step.active .step-number {
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
  box-shadow: 0 4px 8px rgba(0, 78, 80, 0.2);
}

.step-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--an-text-secondary);
  text-align: center;
}

.step.active .step-label {
  color: var(--an-primary);
}

.step-connector {
  width: 40px;
  height: 2px;
  background: var(--an-gray3);
  margin: 0 8px;
}

.info-form-card {
  border: 1px solid var(--an-gray3);
  border-radius: 16px;
  box-shadow: var(--an-card-shadow);
  background: var(--an-white);
  overflow: hidden;
}

.client-form {
  padding: 32px;
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin-bottom: 8px;
}

.required-indicator {
  color: var(--an-error);
  font-weight: 700;
}

.input-wrapper {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 14px 16px 14px 48px;
  border: 2px solid var(--an-gray3);
  border-radius: 10px;
  font-size: 1rem;
  font-family: "Satoshi-Variable", sans-serif;
  background: var(--an-white);
  color: var(--an-text-primary);
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: var(--an-primary);
  box-shadow: 0 0 0 3px rgba(0, 78, 80, 0.1);
}

.form-input.error {
  border-color: var(--an-error);
  background: var(--an-error-background);
}

.form-input:disabled {
  background: var(--an-gray1);
  cursor: not-allowed;
}

.input-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--an-text-secondary);
  font-size: 1rem;
}

.error-message {
  color: var(--an-error);
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.privacy-notice {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: var(--an-info-background);
  border: 1px solid var(--an-info);
  border-radius: 10px;
  margin-bottom: 24px;
}

.notice-icon {
  color: var(--an-info);
  font-size: 1.2rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.notice-text p {
  margin: 0;
  font-size: 0.875rem;
  color: var(--an-text-secondary);
  line-height: 1.5;
}

.notice-text strong {
  color: var(--an-text-primary);
}

.form-actions {
  text-align: center;
}

.continue-button {
  padding: 16px 40px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
  border: none;
  box-shadow: 0 4px 12px rgba(0, 78, 80, 0.25);
  transition: all 0.3s ease;
  cursor: pointer;
  margin-bottom: 12px;
}

.continue-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 78, 80, 0.3);
}

.continue-button:disabled {
  background: var(--an-gray2);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.form-note {
  color: var(--an-text-secondary);
  font-size: 0.875rem;
  margin: 0;
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .client-info-form {
    padding: 0 16px;
  }

  .form-title {
    font-size: 1.75rem;
  }

  .client-form {
    padding: 24px;
  }

  .progress-steps {
    max-width: 300px;
  }

  .step-label {
    font-size: 0.7rem;
  }

  .step-connector {
    width: 30px;
  }
}

@media (max-width: 480px) {
  .form-title {
    font-size: 1.5rem;
  }

  .form-subtitle {
    font-size: 1rem;
  }

  .client-form {
    padding: 20px;
  }
}
</style>
